import { useCallback, useMemo } from 'react';
import Cookies from 'js-cookie';
import useSWR, { mutate as globalMutate } from 'swr';
import { APIGetVehicleTypes } from '../../../config/restAPI/FetchAPI';
import { APICreateVehicleType } from '../../../config/restAPI/PostAPI';
import { APIUpdateVehicleType } from '../../../config/restAPI/PatchAPI';
import {
    extractVehicleTypeData,
    buildVehicleTypeOptions,
    groupVehicleTypeOptions,
    buildVehicleClassByName,
    FALLBACK_VEHICLE_TYPE_OPTIONS,
    normalizeVehicleClassKey,
} from './vehicleTypeOptions';

const SWR_KEY_PREFIX = 'vc-vehicle-types';
// Separate cache entry: the manage dialog needs deactivated types too, and the dropdown must never
// show them. `includeInactive` is honoured for System Administrators only (device-service ignores it
// for everyone else), so a non-admin simply gets the active list here as well.
const SWR_KEY_PREFIX_ALL = 'vc-vehicle-types-all';

const noop = () => {};

/**
 * Whether an API-wrapper result represents success.
 *
 * Two layers have to agree, and checking only one of them is wrong:
 *  - the wrappers resolve axios errors instead of throwing, so a rejected request arrives here as the
 *    resolved value with the real status under `response.status`;
 *  - express-app's `handleResponse` sends its ERROR branch with HTTP 200 and the real status in the
 *    body (`{ status: 500, ... }`), so an HTTP-only check reads a gRPC-layer failure as success.
 * This mirrors the `result?.data?.status !== 200` convention used elsewhere in the repo.
 */
const getResultError = (result, fallbackMessage) => {
    const httpStatus = result?.status ?? result?.response?.status;
    const body = result?.response?.data ?? result?.data;
    const envelopeStatus = typeof body?.status === 'number' ? body.status : null;

    if (!httpStatus || httpStatus >= 400 || (envelopeStatus !== null && envelopeStatus >= 400)) {
        return new Error(body?.error?.message || body?.message || fallbackMessage);
    }
    return null;
};

// Throws on failure so SWR's `error` is meaningful — the wrappers never reject on their own, so
// without this every failure would look like an empty list.
const vehicleTypesFetcher = async ([, token, agencyId]) => {
    const result = await APIGetVehicleTypes(token, agencyId);
    const error = getResultError(result, 'Could not load vehicle types');
    if (error) throw error;
    return extractVehicleTypeData(result);
};

const allVehicleTypesFetcher = async ([, token, agencyId]) => {
    const result = await APIGetVehicleTypes(token, agencyId, { includeInactive: true });
    const error = getResultError(result, 'Could not load vehicle types');
    if (error) throw error;
    return extractVehicleTypeData(result);
};

/**
 * Vehicle types for the preset form dropdown, plus `addVehicleType` for the inline add control.
 *
 * Modelled on `useRegistryFuelTariffOptions`. On a failed fetch the offline fallback list keeps the
 * form usable; note that an empty list from a SUCCESSFUL fetch is NOT treated as failure — that means
 * the collection has not been seeded, and showing the fallback five there would look populated while
 * every save fails against an empty server-side allow-list.
 */
export const useVehicleTypeOptions = () => {
    const token = Cookies.get('jwtToken');
    const agencyId = Cookies.get('agencyUUID');
    const enabled = Boolean(token && agencyId);

    // Memoized: a fresh array literal each render would give `addVehicleType` a new identity every
    // render, defeating the useMemo below and handing react-select a new `options` array each time.
    const swrKey = useMemo(
        () => (enabled ? [SWR_KEY_PREFIX, token, agencyId] : null),
        [enabled, token, agencyId],
    );

    const { data, error, isValidating } = useSWR(swrKey, vehicleTypesFetcher, {
        revalidateOnFocus: false,
    });

    const addVehicleType = useCallback(async (name, { vehicleClass } = {}) => {
        const trimmed = String(name || '').trim();
        if (!trimmed) throw new Error('Enter a name');
        const classKey = normalizeVehicleClassKey(vehicleClass);
        if (!classKey) throw new Error('Select a vehicle class');
        // Throw rather than resolve: resolving would report a type as added when no request was made.
        if (!enabled) throw new Error('Your session has expired. Please sign in again.');

        // Persist the fleetconsole class token (BIKE, CAR_SUV, VAN_LTRUCK, HEAVY_TRUCK, BUS).
        // GET responses may still use seeded type names; normalizeVehicleClassKey maps those back.
        const result = await APICreateVehicleType(token, noop, agencyId, {
            name: trimmed,
            vehicleClass: classKey,
        });
        // 409 (duplicate) is the one users will actually hit; the server's message names the clash.
        const requestError = getResultError(result, 'Could not add the vehicle type');
        if (requestError) throw requestError;

        if (swrKey) await globalMutate(swrKey);
        return trimmed;
    }, [enabled, token, agencyId, swrKey]);

    return useMemo(() => {
        const fetched = buildVehicleTypeOptions(data);
        const flatOptions = error ? FALLBACK_VEHICLE_TYPE_OPTIONS : fetched;
        const options = groupVehicleTypeOptions(flatOptions);
        const validValues = new Set(flatOptions.map((option) => option.value));
        const vehicleClassByName = buildVehicleClassByName(flatOptions);
        const loading = enabled && !data && !error && isValidating;

        return {
            vehicleTypeOptions: options,
            vehicleClassByName,
            validValues,
            loading,
            error,
            addVehicleType,
        };
    }, [enabled, data, error, isValidating, addVehicleType]);
};

/**
 * Vehicle types for the manage dialog: every type including deactivated ones, plus the toggle.
 *
 * Kept separate from `useVehicleTypeOptions` so the preset dropdown can never accidentally offer a
 * deactivated type. Mount this only while the dialog is open — `enabled` gates the fetch.
 */
export const useManageVehicleTypes = (enabled = true) => {
    const token = Cookies.get('jwtToken');
    const agencyId = Cookies.get('agencyUUID');
    const ready = Boolean(token && agencyId) && enabled;

    const allKey = useMemo(
        () => (ready ? [SWR_KEY_PREFIX_ALL, token, agencyId] : null),
        [ready, token, agencyId],
    );

    const {
        data, error, isValidating, mutate,
    } = useSWR(allKey, allVehicleTypesFetcher, { revalidateOnFocus: false });

    const setVehicleTypeActive = useCallback(async (oid, isActive) => {
        if (!oid) throw new Error('Missing vehicle type id');
        if (!ready) throw new Error('Your session has expired. Please sign in again.');

        const result = await APIUpdateVehicleType(token, noop, agencyId, { oid, isActive });
        // The server refuses to deactivate a built-in type; surface that message rather than a generic one.
        const requestError = getResultError(result, 'Could not update the vehicle type');
        if (requestError) throw requestError;

        // Revalidate both lists: this view, and the dropdown that must stop (or start) offering it.
        await mutate();
        await globalMutate([SWR_KEY_PREFIX, token, agencyId]);
    }, [ready, token, agencyId, mutate]);

    return useMemo(() => ({
        vehicleTypes: Array.isArray(data) ? data : [],
        loading: ready && !data && !error && isValidating,
        error,
        setVehicleTypeActive,
    }), [ready, data, error, isValidating, setVehicleTypeActive]);
};
